import React, { useState } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, Button,
  InputAdornment, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, MenuItem,
  Select, FormControl, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Divider, Tooltip, Badge
} from '@mui/material';
import {
  Search, FileDownload, PhoneEnabled, HistoryEdu,
  Done, Close, ArrowForward, Whatshot, Facebook,
  WhatsApp, Groups, DirectionsWalk, History, Add,
  FiberManualRecord
} from '@mui/icons-material';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'follow',        label: 'Follow',        color: '#1a237e', bg: '#e8f0fe' },
  { value: 'success_close', label: 'Success Close',  color: '#2e7d32', bg: '#e8f5e9' },
  { value: 'cancel',        label: 'Cancel',         color: '#c62828', bg: '#ffebee' },
];

const MEDIA_OPTIONS = [
  { value: 'call',     label: 'Call',      color: '#2e7d32', bg: '#e8f5e9',  icon: <PhoneEnabled sx={{ fontSize: 12 }} /> },
  { value: 'wp',       label: 'WhatsApp',  color: '#1a6e3e', bg: '#e8f5ee',  icon: <WhatsApp sx={{ fontSize: 12 }} /> },
  { value: 'facebook', label: 'Facebook',  color: '#1565c0', bg: '#e7f0ff',  icon: <Facebook sx={{ fontSize: 12 }} /> },
  { value: 'visit',    label: 'Visit',     color: '#e65100', bg: '#fff3e0',  icon: <DirectionsWalk sx={{ fontSize: 12 }} /> },
  { value: 'meeting',  label: 'Meeting',   color: '#6a1b9a', bg: '#f3e5f5',  icon: <Groups sx={{ fontSize: 12 }} /> },
];

const PRIORITY_OPTIONS = [
  { value: 'high',   label: 'High',   color: '#c62828', bg: '#ffebee' },
  { value: 'medium', label: 'Medium', color: '#e65100', bg: '#fff3e0' },
  { value: 'low',    label: 'Low',    color: '#2e7d32', bg: '#e8f5e9' },
];

const AGENTS = ['Priya M.', 'Suresh T.', 'Amit D.', 'Riya S.'];

const INITIAL_ROWS = [
  { id: 1, name: 'Rahul Shinde',    phone: '98765 43210', source: 'Facebook', campaign: 'Hearing Care March',  classification: 'Hot Leads', dials: 2, maxDials: 5, last: 'Today 11:20 AM', sub: '2 hrs ago',  assigned: 'Priya M.',  status: 'New',       isConv: false },
  { id: 2, name: 'Meena Kulkarni',  phone: '91234 56789', source: 'Facebook', campaign: 'HA Awareness Camp',   classification: 'Hot Leads', dials: 3, maxDials: 5, last: 'Today 10:05 AM', sub: '3 hrs ago',  assigned: 'Suresh T.', status: 'Called',    isConv: false },
  { id: 3, name: 'Amol Patil',      phone: '97654 32109', source: 'Facebook', campaign: 'Hearing Care March',  classification: 'Warm',      dials: 4, maxDials: 5, last: 'Yesterday 4:30 PM', sub: '20 hrs ago', assigned: 'Amit D.',  status: 'No Answer', isConv: false },
  { id: 4, name: 'Sunita Kadam',    phone: '93456 78901', source: 'Walk-in',  campaign: '—',                   classification: 'Hot Leads', dials: 2, maxDials: 5, last: 'Today 9:00 AM', sub: '4 hrs ago',   assigned: 'Riya S.',  status: 'Follow-Up', isConv: false },
  { id: 5, name: 'Nandini Bhosale', phone: '87654 32109', source: 'Facebook', campaign: 'HA Awareness Camp',   classification: 'Warm',      dials: 3, maxDials: 5, last: '19 Mar 2:00 PM', sub: '2 days ago',  assigned: 'Amit D.',  status: 'Converted', isConv: true  },
];

const INITIAL_HISTORY = {
  1: [
    { id: 1, date: '2 May 3:00 PM',   media: 'wp',       status: 'follow',  assigned: 'Priya M.',  note: 'Sent brochure on WA, awaiting reply' },
    { id: 2, date: '28 Apr 10:30 AM', media: 'facebook', status: 'follow',  assigned: 'Suresh T.', note: 'First contact via FB Messenger' },
  ],
  2: [
    { id: 1, date: '29 Apr 11:00 AM', media: 'call',     status: 'follow',  assigned: 'Suresh T.', note: 'Discussed product options' },
  ],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getClassStyle = (cls) => {
  if (cls === 'Hot Leads') return { color: '#d32f2f', bg: '#ffebee' };
  if (cls === 'Warm')      return { color: '#ed6c02', bg: '#fff3e0' };
  return { color: '#1976d2', bg: '#e3f2fd' };
};

const getStatusStyle = (status) => {
  const map = {
    'New':       { color: '#1976d2', bg: '#e3f2fd' },
    'Called':    { color: '#ed6c02', bg: '#fff3e0' },
    'No Answer': { color: '#64748B', bg: '#f1f5f9' },
    'Follow-Up': { color: '#2e7d32', bg: '#e8f5e9' },
    'Converted': { color: '#2e7d32', bg: '#e8f5e9' },
  };
  return map[status] || { color: '#64748B', bg: '#f1f5f9' };
};

const getMediaOption = (val) => MEDIA_OPTIONS.find((m) => m.value === val) || MEDIA_OPTIONS[0];
const getStatusOption = (val) => STATUS_OPTIONS.find((s) => s.value === val) || STATUS_OPTIONS[0];
const getPriorityOption = (val) => PRIORITY_OPTIONS.find((p) => p.value === val) || PRIORITY_OPTIONS[0];

const today = () => new Date().toISOString().split('T')[0];
const nowLabel = () => {
  const d = new Date();
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const MediaPill = ({ value }) => {
  const opt = getMediaOption(value);
  return (
    <Chip
      icon={React.cloneElement(opt.icon, { style: { color: opt.color, marginLeft: 6 } })}
      label={opt.label}
      size="small"
      sx={{ bgcolor: opt.bg, color: opt.color, fontWeight: 600, fontSize: '0.65rem', border: 'none' }}
    />
  );
};

const StatusPill = ({ value }) => {
  const opt = getStatusOption(value);
  return (
    <Chip
      label={opt.label}
      size="small"
      sx={{ bgcolor: opt.bg, color: opt.color, fontWeight: 600, fontSize: '0.65rem' }}
    />
  );
};

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.5px', mb: 0.5, textTransform: 'uppercase' }}>
    {children}
  </Typography>
);

// ─── Follow-Up Modal ──────────────────────────────────────────────────────────

const FollowUpModal = ({ open, onClose, lead, history, onSave }) => {
  const [form, setForm] = useState({
    status: 'follow',
    media: 'call',
    assigned: lead?.assigned || AGENTS[0],
    followDate: today(),
    priority: 'high',
    note: '',
  });

  React.useEffect(() => {
    if (lead) setForm((f) => ({ ...f, assigned: lead.assigned }));
  }, [lead]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = () => {
    onSave(lead.id, { ...form, date: nowLabel(), id: Date.now() });
    onClose();
  };

  const leadHistory = history[lead?.id] || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      {/* ── Header ── */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2.5, borderBottom: '1px solid #f0f0f0', bgcolor: '#fafbff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArrowForward sx={{ color: '#1a237e', fontSize: 18 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1a237e' }}>
            Convert Lead to Follow-Up
          </Typography>
          <Chip label="Follow-Up" size="small" sx={{ bgcolor: '#e8f0fe', color: '#1a237e', fontWeight: 700, fontSize: '0.65rem' }} />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* ── Lead chip ── */}
        <Box sx={{ mx: 2.5, mt: 2, mb: 1.5, p: 1.5, bgcolor: '#f8faff', borderRadius: '12px', border: '1px solid #e8f0fe', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#1976d2', width: 36, height: 36, fontSize: '0.85rem' }}>
            {lead?.name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{lead?.name}</Typography>
              {lead?.classification === 'Hot Leads' && (
                <Chip icon={<Whatshot sx={{ fontSize: '12px !important', color: '#d32f2f !important' }} />} label="Hot" size="small" sx={{ bgcolor: '#ffebee', color: '#d32f2f', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
              )}
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
              {lead?.phone} &nbsp;•&nbsp; {lead?.source} Lead &nbsp;•&nbsp; {lead?.campaign}
            </Typography>
          </Box>
          <Chip label={lead?.classification} size="small" sx={{ bgcolor: getClassStyle(lead?.classification).bg, color: getClassStyle(lead?.classification).color, fontWeight: 700, fontSize: '0.65rem' }} />
        </Box>

        <Divider sx={{ mx: 2.5 }} />

        {/* ── Form ── */}
        <Grid container spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1 }}>

          {/* Row 1 */}
          <Grid item xs={12} sm={4}>
            <FieldLabel>Status</FieldLabel>
            <FormControl fullWidth size="small">
              <Select value={form.status} onChange={set('status')} sx={{ borderRadius: '8px' }}>
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FiberManualRecord sx={{ fontSize: 10, color: o.color }} />
                      {o.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FieldLabel>Communication Media</FieldLabel>
            <FormControl fullWidth size="small">
              <Select value={form.media} onChange={set('media')} sx={{ borderRadius: '8px' }}
                renderValue={(val) => {
                  const opt = getMediaOption(val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(opt.icon, { style: { color: opt.color } })}
                      {opt.label}
                    </Box>
                  );
                }}
              >
                {MEDIA_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(o.icon, { style: { color: o.color } })}
                      {o.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FieldLabel>Assigned To</FieldLabel>
            <FormControl fullWidth size="small">
              <Select value={form.assigned} onChange={set('assigned')} sx={{ borderRadius: '8px' }}>
                {AGENTS.map((a) => (
                  <MenuItem key={a} value={a}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: '#1976d2' }}>{a[0]}</Avatar>
                      {a}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} sm={4}>
            <FieldLabel>Next Follow-Up Date</FieldLabel>
            <TextField
              type="date"
              fullWidth
              size="small"
              value={form.followDate}
              onChange={set('followDate')}
              sx={{ '& fieldset': { borderRadius: '8px' } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FieldLabel>Priority</FieldLabel>
            <FormControl fullWidth size="small">
              <Select value={form.priority} onChange={set('priority')} sx={{ borderRadius: '8px' }}
                renderValue={(val) => {
                  const opt = getPriorityOption(val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FiberManualRecord sx={{ fontSize: 10, color: opt.color }} />
                      {opt.label}
                    </Box>
                  );
                }}
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FiberManualRecord sx={{ fontSize: 10, color: o.color }} />
                      {o.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 3 – full width note */}
          <Grid item xs={12}>
            <FieldLabel>Note & Remark</FieldLabel>
            <TextField
              multiline
              rows={2}
              fullWidth
              placeholder="Add your note or remark about this follow-up..."
              value={form.note}
              onChange={set('note')}
              sx={{ '& fieldset': { borderRadius: '8px' } }}
            />
          </Grid>
        </Grid>

        {/* ── Communication History ── */}
        <Box sx={{ mx: 2.5, mt: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
            <History sx={{ fontSize: 15, color: '#64748B' }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748B', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              History of Communication
            </Typography>
            <Chip label={leadHistory.length} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#e8f0fe', color: '#1a237e', ml: 0.5 }} />
          </Box>

          {leadHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#fafafa', borderRadius: '10px', border: '1px dashed #e0e0e0' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>No communication history yet</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '10px', border: '1px solid #E2E8F0', maxHeight: 220, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['#', 'Name', 'Date', 'Media', 'Status', 'Assigned To', 'Note & Remark'].map((h) => (
                      <TableCell key={h} sx={{ bgcolor: '#F8FAFC', fontWeight: 700, color: '#64748B', fontSize: '0.62rem', letterSpacing: '0.4px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leadHistory.map((h, i) => (
                    <TableRow key={h.id} sx={{ '&:hover': { bgcolor: '#fafbff' } }}>
                      <TableCell sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: '#1976d2' }}>
                            {lead?.name?.[0]}
                          </Avatar>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{lead?.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: '#64748B', whiteSpace: 'nowrap' }}>{h.date}</TableCell>
                      <TableCell><MediaPill value={h.media} /></TableCell>
                      <TableCell><StatusPill value={h.status} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Avatar sx={{ width: 18, height: 18, fontSize: '0.55rem', bgcolor: '#7b1fa2' }}>
                            {h.assigned?.[0]}
                          </Avatar>
                          <Typography sx={{ fontSize: '0.7rem' }}>{h.assigned}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', color: '#64748B', maxWidth: 180 }}>
                        <Tooltip title={h.note} arrow placement="top">
                          <span style={{ cursor: 'default', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                            {h.note}
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f0f0f0', bgcolor: '#fafbff', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small"
          sx={{ borderRadius: '8px', textTransform: 'none', color: '#64748B', borderColor: '#E2E8F0', fontSize: '0.8rem' }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" size="small" startIcon={<Done sx={{ fontSize: 14 }} />}
          sx={{ borderRadius: '8px', textTransform: 'none', bgcolor: '#1a237e', fontSize: '0.8rem', fontWeight: 700,
            '&:hover': { bgcolor: '#0d1442' } }}>
          Save Follow-Up
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const LeadContent = () => {
  const [rows, setRows] = useState(INITIAL_ROWS);
  const [history, setHistory] = useState(INITIAL_HISTORY);
  const [open, setOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterClass, setFilterClass] = useState('');

  const handleOpenFollowUp = (lead) => { setSelectedLead(lead); setOpen(true); };
  const handleClose = () => setOpen(false);

  const handleSave = (leadId, entry) => {
    setHistory((prev) => ({
      ...prev,
      [leadId]: [{ ...entry }, ...(prev[leadId] || [])],
    }));
    setRows((prev) =>
      prev.map((r) =>
        r.id === leadId
          ? { ...r, status: 'Follow-Up', last: nowLabel(), sub: 'Just now', assigned: entry.assigned }
          : r
      )
    );
  };

  const filtered = rows.filter((r) => {
    const matchSearch = !search || r.name.toLowerCase().includes(search.toLowerCase()) || r.phone.includes(search);
    const matchSource = !filterSource || r.source === filterSource;
    const matchClass  = !filterClass  || r.classification === filterClass;
    return matchSearch && matchSource && matchClass;
  });

  return (
    <Box sx={{ width: '100%' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e' }}>Follow-Up Management</Typography>
          <Typography variant="caption" sx={{ color: '#64748B' }}>Track and manage all your leads</Typography>
        </Box>
      </Box>

      {/* ── Filter Bar ── */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: '15px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search name, mobile..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>, sx: { borderRadius: '10px', bgcolor: '#F8FAFC' } }}
          sx={{ width: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select displayEmpty value={filterSource} onChange={(e) => setFilterSource(e.target.value)} sx={{ borderRadius: '10px' }}>
            <MenuItem value="">All Sources</MenuItem>
            <MenuItem value="Facebook">Facebook</MenuItem>
            <MenuItem value="Walk-in">Walk-in</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select displayEmpty value={filterClass} onChange={(e) => setFilterClass(e.target.value)} sx={{ borderRadius: '10px' }}>
            <MenuItem value="">All Classes</MenuItem>
            <MenuItem value="Hot Leads">Hot Leads</MenuItem>
            <MenuItem value="Warm">Warm</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<FileDownload />} sx={{ ml: 'auto', borderRadius: '10px', textTransform: 'none', color: '#64748B', borderColor: '#E2E8F0' }}>
          Export
        </Button>
      </Paper>

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '15px', border: '1px solid #E2E8F0', maxHeight: 'calc(100vh - 260px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {['#', 'LEAD NAME', 'SOURCE', 'AD CAMPAIGN', 'CLASSIFICATION', 'DIALS', 'LAST CONTACT', 'ASSIGNED TO', 'STATUS', 'ACTIONS'].map((h) => (
                <TableCell key={h} sx={{ bgcolor: '#F8FAFC', fontWeight: 700, color: '#64748B', fontSize: '0.68rem', letterSpacing: '0.4px' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => {
              const cls = getClassStyle(row.classification);
              const sts = getStatusStyle(row.status);
              const histCount = (history[row.id] || []).length;
              return (
                <TableRow key={row.id} sx={{ bgcolor: row.isConv ? '#f0fdf4' : 'white', '&:hover': { bgcolor: row.isConv ? '#e6f9ee' : '#fafbff' } }}>
                  <TableCell sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{row.id}</TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge badgeContent={histCount || null} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14 } }}>
                        <Avatar sx={{ bgcolor: '#1976d2', width: 28, height: 28, fontSize: '0.72rem' }}>{row.name[0]}</Avatar>
                      </Badge>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{row.name}</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8' }}>{row.phone}</Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip label={row.source} size="small" variant="outlined" sx={{ fontSize: '0.65rem', color: '#1976d2', bgcolor: '#f0f7ff', borderColor: '#b3d1f5' }} />
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.72rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.campaign}</TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FiberManualRecord sx={{ fontSize: 8, color: cls.color }} />
                      <Chip label={row.classification} size="small" sx={{ bgcolor: cls.bg, color: cls.color, fontSize: '0.65rem', fontWeight: 700 }} />
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      {[1, 2, 3, 4, 5].map((d) => (
                        <Box key={d} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: d <= row.dials ? '#1a237e' : '#CBD5E1' }} />
                      ))}
                      <Typography sx={{ fontSize: '0.62rem', ml: 0.5, color: '#64748B' }}>{row.dials}/{row.maxDials}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{row.last}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#94A3B8' }}>{row.sub}</Typography>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.55rem', bgcolor: '#7b1fa2' }}>{row.assigned[0]}</Avatar>
                      <Typography sx={{ fontSize: '0.72rem' }}>{row.assigned}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      icon={row.isConv ? <Done sx={{ fontSize: '12px !important' }} /> : null}
                      label={row.status}
                      size="small"
                      sx={{ bgcolor: sts.bg, color: sts.color, fontSize: '0.65rem', fontWeight: 700 }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                      <Button variant="outlined" size="small" startIcon={<PhoneEnabled sx={{ fontSize: 11 }} />}
                        sx={{ fontSize: '0.62rem', textTransform: 'none', color: '#2e7d32', borderColor: '#A5D6A7', minWidth: 50, px: 1 }}>
                        Dial
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<HistoryEdu sx={{ fontSize: 11 }} />}
                        sx={{ fontSize: '0.62rem', textTransform: 'none', color: '#7b1fa2', borderColor: '#CE93D8', minWidth: 44, px: 1 }}>
                        Log
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpenFollowUp(row)}
                        startIcon={row.isConv ? <Done sx={{ fontSize: 11 }} /> : <ArrowForward sx={{ fontSize: 11 }} />}
                        sx={{ fontSize: '0.62rem', textTransform: 'none', bgcolor: row.isConv ? '#2e7d32' : '#1a237e', minWidth: 80, px: 1,
                          '&:hover': { bgcolor: row.isConv ? '#1b5e20' : '#0d1442' } }}
                      >
                        {row.isConv ? 'View Log' : 'Follow-Up'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Modal ── */}
      {selectedLead && (
        <FollowUpModal
          open={open}
          onClose={handleClose}
          lead={selectedLead}
          history={history}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export default LeadContent;